import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { ChevronLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import apiService from "../services/api";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerAvatars = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [appliedAvatar, setAppliedAvatar] = useState(null);
  const [saving, setSaving] = useState(false);

  const profile = useMemo(
    () =>
      location.state?.profile ||
      JSON.parse(sessionStorage.getItem("selectedProfile") || "{}"),
    [location.state?.profile]
  );
  const profileName = profile?.name || profile?.profileName || "Profile";
  const fetchAvatarsRef = useRef(null);
  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    fetchAvatarsRef.current?.();
    // Get current avatar from profile or user settings
    const currentAvatar =
      profile?.avatarUrl ||
      profile?.avatar ||
      sessionStorage.getItem("userAvatar");

    setSelectedAvatar(currentAvatar);
    setAppliedAvatar(currentAvatar);
  }, [profile?.avatar, profile?.avatarUrl]);

  // Close on Escape (no nav rail on this route)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") handleCancel();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleCancel]);

  const fetchAvatars = async () => {
    try {
      setLoading(true);
      const response = await apiService.webPlayerGetAvatars();

      if (response?.data) {
        setAvatars(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch avatars:", error);
      toast.error("Failed to load avatars");
    } finally {
      setLoading(false);
    }
  };

  fetchAvatarsRef.current = fetchAvatars;

  const saveAvatar = async (avatarUrl) => {
    if (!avatarUrl) {
      toast.error("Please select an avatar");

      return;
    }

    try {
      setSaving(true);
      setSelectedAvatar(avatarUrl);

      // Resolve the profile id from every available source. The web maps
      // profileId -> id (WebPlayerProfiles), but the profile passed via
      // navigation state can lack it, so fall back through all of them.
      let storedId = null;

      try {
        storedId = JSON.parse(
          sessionStorage.getItem("selectedProfile") || "{}"
        )?.id;
      } catch {
        storedId = null;
      }

      const profileId =
        profile?.id ||
        profile?.profileId ||
        storedId ||
        webPlayerStorage.getProfileId();

      // Persist locally FIRST so the user-info overlay + nav avatar reflect the
      // new avatar immediately — independent of the backend call.
      const updatedProfile = {
        ...profile,
        id: profileId,
        avatar: avatarUrl,
        avatarUrl,
      };

      webPlayerStorage.setProfile(updatedProfile);
      sessionStorage.setItem("selectedProfile", JSON.stringify(updatedProfile));
      sessionStorage.setItem("userAvatar", avatarUrl);

      // Notify already-mounted components right away (e.g. the nav avatar)
      window.dispatchEvent(
        new CustomEvent("profileUpdated", {
          detail: { profile: updatedProfile },
        })
      );

      // Persist to the backend — same endpoint/payload as the Android app:
      // PATCH /v2/user/profile/{id} with { avatarUrl }.
      if (profileId) {
        await apiService.webPlayerUpdateProfile(profileId, { avatarUrl });

        const loginAuth = webPlayerStorage.getLoginAuth();

        if (loginAuth?.profiles) {
          loginAuth.profiles = loginAuth.profiles.map((p) =>
            p.profileId === profileId ? { ...p, avatarUrl } : p
          );
          webPlayerStorage.setLoginAuth(loginAuth);
        }
      } else {
        console.warn("No profile id resolved — avatar not saved to backend");
      }

      toast.success("Avatar updated successfully");

      // Navigate back to the previous page (e.g. the user-info overlay)
      if (location.state?.from === "userInfo") {
        navigate(-1);
      } else {
        navigate("/web-player/home");
      }

      // Re-dispatch after navigation in case the target just mounted
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("profileUpdated", {
            detail: { profile: updatedProfile },
          })
        );
      }, 100);
    } catch (error) {
      console.error("Failed to update avatar:", error);
      toast.error("Failed to update avatar");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => saveAvatar(selectedAvatar);

  const isApplied = selectedAvatar && selectedAvatar === appliedAvatar;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-950 to-black">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-6 p-6 lg:h-screen lg:flex-row lg:overflow-hidden">
        {/* Left - selection / preview card (glass) */}
        <aside className="relative flex w-full shrink-0 flex-col rounded-3xl border border-white/15 bg-gray-900/40 p-6 shadow-2xl ring-1 ring-inset ring-white/5 backdrop-blur-2xl lg:w-[360px] lg:self-center">
          <button
            aria-label="Back"
            className="absolute left-4 top-4 text-gray-400 transition-colors hover:text-white"
            onClick={handleCancel}
          >
            <ChevronLeft size={24} />
          </button>

          <div className="mb-4 mt-2 text-center text-xs font-semibold tracking-[0.2em] text-yellow-500">
            ✓ SELECTED
          </div>

          {/* Preview avatar */}
          <div className="flex justify-center">
            <div className="rounded-full bg-gradient-to-br from-[#6495ED] via-purple-400 to-yellow-400 p-[3px]">
              <img
                alt="Selected avatar"
                className="h-44 w-44 rounded-full bg-gray-900 object-cover"
                src={selectedAvatar || "/default-avatar.png"}
                onError={(e) => {
                  e.target.src = "/default-avatar.png";
                }}
              />
            </div>
          </div>

          <div className="mt-4 text-center">
            <div className="text-xl font-bold text-white">Avatar</div>
            <div className="text-sm text-gray-400">
              {isApplied ? "Currently selected" : "New selection"}
            </div>
          </div>

          <div className="my-5 border-t border-white/10" />

          <div className="mb-3 text-xs tracking-[0.2em] text-gray-400">
            WILL APPEAR AS
          </div>

          {/* Welcome back row */}
          <div className="mb-3 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
            <img
              alt="Profile avatar"
              className="h-10 w-10 rounded-full object-cover"
              src={selectedAvatar || "/default-avatar.png"}
              onError={(e) => {
                e.target.src = "/default-avatar.png";
              }}
            />
            <div className="min-w-0">
              <div className="text-[11px] tracking-wider text-gray-400">
                WELCOME BACK
              </div>
              <div className="truncate font-semibold text-white">
                {profileName}
              </div>
            </div>
          </div>

          {/* Profile + status cells */}
          <div className="mb-5 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="text-[11px] tracking-wider text-gray-400">
                PROFILE
              </div>
              <div className="truncate font-medium text-white">
                {profileName}
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="text-[11px] tracking-wider text-gray-400">
                STATUS
              </div>
              <div className="font-medium text-white">
                {isApplied ? "In use" : "Preview"}
              </div>
            </div>
          </div>

          {/* Confirm button */}
          <button
            className={`mt-auto w-full rounded-full py-3 font-semibold transition-colors disabled:opacity-60 ${
              isApplied
                ? "bg-white text-gray-900 hover:bg-gray-100"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
            disabled={saving}
            onClick={isApplied ? handleCancel : handleSave}
          >
            {saving
              ? "Saving..."
              : isApplied
                ? "✓ Selected"
                : "Use this Avatar"}
          </button>
        </aside>

        {/* Right - avatar grid */}
        <main className="flex-1 lg:overflow-y-auto">
          <h1 className="mb-5 text-xs font-semibold tracking-[0.2em] text-gray-400">
            CHOOSE YOUR AVATAR
          </h1>

          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-500" />
              <p className="ml-4 text-white">Loading avatars...</p>
            </div>
          ) : avatars.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-xl text-gray-400">No avatars available</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-5 sm:grid-cols-5 md:grid-cols-6 xl:grid-cols-7">
              {avatars.map((avatar, index) => {
                const isSelected = selectedAvatar === avatar;

                return (
                  <button
                    aria-label={`Select avatar ${index + 1}`}
                    className="group relative aspect-square"
                    key={index}
                    onClick={() => saveAvatar(avatar)}
                  >
                    <div
                      className={`h-full w-full overflow-hidden rounded-full transition-all duration-300 ${
                        isSelected
                          ? "scale-105 ring-4 ring-[#6495ED]"
                          : "ring-2 ring-transparent group-hover:ring-white/30"
                      }`}
                    >
                      <img
                        alt={`Avatar ${index + 1}`}
                        className="h-full w-full object-cover"
                        src={avatar}
                        onError={(e) => {
                          e.target.src = "/default-avatar.png";
                        }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default WebPlayerAvatars;
